import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const LOGO_URL =
  "https://customer-assets-lqy194kg.emergentagent.net/job_magic-link-journey/artifacts/zmxbnelr_Reelty_Logo_R-Only.png";

export default function VerifyPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [state, setState] = useState("verifying");
  const [message, setMessage] = useState("");
  const attempted = useRef(false);

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setState("error");
      setMessage("This link is missing a token.");
      return;
    }
    if (attempted.current) return;
    attempted.current = true;

    (async () => {
      try {
        const { data } = await api.post("/auth/verify-magic-link", { token });
        await loginWithToken(data.access_token, data.user);
        setState("success");
        toast.success(`Welcome${data.user.name ? ", " + data.user.name : ""}!`);
        setTimeout(() => {
          navigate(data.user.role === "admin" ? "/admin" : "/dashboard", { replace: true });
        }, 600);
      } catch (err) {
        const raw = err?.response?.data?.detail || "";
        // The backend's decode_token returns "Token has expired" verbatim — map
        // it to friendly copy so the user knows exactly what to do next.
        const isExpired = /expired/i.test(raw);
        setState("error");
        setMessage(
          isExpired
            ? "This magic link has expired. Request a new one from the sign-in page — it takes 5 seconds."
            : raw || "This magic link is invalid. Request a new one from the sign-in page.",
        );
      }
    })();
  }, [params, loginWithToken, navigate]);

  return (
    <div className="min-h-screen bg-[#F5EBD9] text-[#1A0F0A] flex items-center justify-center p-6 grain-overlay" data-testid="verify-page">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative z-10 max-w-md w-full card-surface rounded-sm p-10 text-center"
      >
        <img src={LOGO_URL} alt="Reelty" className="w-12 h-12 object-contain mx-auto mb-6 opacity-90" />
        {state === "verifying" && (
          <>
            <Loader2 className="w-8 h-8 text-[#6B0F1A] animate-spin mx-auto mb-6" strokeWidth={1.75} />
            <div className="font-mono-tag text-[10px] text-[#78584B] mb-2">Reelty · Verify</div>
            <h1 className="font-display text-3xl font-bold text-[#1A0F0A]">Signing you in…</h1>
            <p className="text-[#4D3F35] mt-3 text-sm">Verifying your magic link.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="w-10 h-10 text-[#6B0F1A] mx-auto mb-6" strokeWidth={1.75} />
            <h1 className="font-display text-3xl font-bold text-[#1A0F0A]">You're in.</h1>
            <p className="text-[#4D3F35] mt-3 text-sm">Redirecting to your portal…</p>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="w-10 h-10 text-[#6B0F1A] mx-auto mb-6" strokeWidth={1.75} />
            <h1 className="font-display text-3xl font-bold text-[#1A0F0A]">Link couldn't be verified</h1>
            <p className="text-[#4D3F35] mt-3 text-sm">{message}</p>
            <button
              onClick={() => navigate("/", { replace: true })}
              data-testid="verify-return-home-btn"
              className="mt-8 font-mono-tag text-[11px] text-[#6B0F1A] hover:text-[#8A1424]"
            >
              ← REQUEST A NEW LINK
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
